import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

import type {BoardCardActionsSource} from '../components/board/issues/card-actions'
import {
  ASSIGN_AGENT_CARD_ACTION,
  isBoardCardActionsPayload,
  type BoardCardActions,
} from '../components/board/issues/card-actions-payload'
import {createBatchLoader} from './create-batch-loader'

/** Gathers the cards a pointer sweeps across, or a menu opened while others load, into one request. */
const BATCH_WINDOW_MS = 50

// Mirrors the server's cap on `card-actions` ids, which refuses a request naming more.
const MAX_IDS_PER_REQUEST = 50

export function createSavedViewCardActionsSource({
  pathname,
  routeId,
  scope,
}: {
  pathname: string
  routeId: string
  scope: BoardCardActionsSource['scope']
}): BoardCardActionsSource {
  const {load} = createBatchLoader({
    fetch: globalIds => fetchCardActions(pathname, routeId, globalIds),
    batchWindowMs: BATCH_WINDOW_MS,
    maxBatchSize: MAX_IDS_PER_REQUEST,
    maxConcurrentRequests: 2,
  })

  return {scope, load}
}

async function fetchCardActions(
  pathname: string,
  routeId: string,
  globalIds: string[],
): Promise<Map<string, BoardCardActions>> {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The board card actions endpoint returns plain JSON validated by `isBoardCardActionsPayload`, not a Protobuf payload.
  const json = await queryFnFetch<unknown>({
    routeId,
    queryName: 'boardCardActions',
    queryDeps: {pathname, searchParams: {ids: globalIds.join(','), actions: ASSIGN_AGENT_CARD_ACTION}},
  })

  if (!isBoardCardActionsPayload(json)) throw new Error('board card actions failed validation')

  return new Map(json.items.map(item => [item.globalId, item.actions]))
}
